import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { useScreenDetector } from '@/shared/lib/hooks/useScreenDetector';
import { BottomSheet } from '@/shared/ui/BottomSheet/BottomSheet';
import { IconButton } from '@/shared/ui/IconButton/IconButton';
import { Button } from '@/shared/ui/Button/Button';
import { ButtonMobile } from '@/shared/ui/Button/ButtonMobile';
import { Overlay } from '@/shared/ui/Overlay/Overlay';
import CloseIcon from '@/shared/assets/icons/close.svg';
import { useLogout } from '../../model/useLogout';
import s from './LogoutModal.module.scss';

interface LogoutModalProps {
  onClose: () => void;
  open: boolean;
}

export const LogoutModal = (props: LogoutModalProps) => {
  const { open, onClose } = props;
  const { isMobile } = useScreenDetector();
  const { logout } = useLogout();
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = modalRef.current;
    if (!el || isMobile) return;
    if (open) disableBodyScroll(el);
    else enableBodyScroll(el);
    return () => {
      if (el) enableBodyScroll(el);
    };
  }, [open, isMobile]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!open || isMobile) return;
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onKeyDown, isMobile]);

  if (!open) return null;

  if (isMobile) {
    return (
      <BottomSheet
        open={open}
        onClose={onClose}
        title="Выйти из аккаунта"
        className={s.mobileSheet}
        contentClassName={s.mobileContent}
      >
        <div className={s.mobileBody}>
          <span className={s.mobileText}>Вы завершите текущую сессию и вернетесь на экран входа</span>
        </div>
        <div className={s.mobileFooter}>
          <ButtonMobile
            variant="secondary"
            color="green"
            fullWidth={false}
            onClick={onClose}
            className={s.mobileCancelBtn}
          >
            Отмена
          </ButtonMobile>
          <ButtonMobile variant="fill" color="green" fullWidth={true} onClick={logout} className={s.mobileLogoutBtn}>
            Выйти
          </ButtonMobile>
        </div>
      </BottomSheet>
    );
  }

  return createPortal(
    <>
      <div className={s.modalWrapper} ref={modalRef} tabIndex={-1}>
        <div className={s.modal}>
          <IconButton size="m" className={s.closeBtn} onClick={onClose} color="black">
            <CloseIcon />
          </IconButton>

          <div className={s.textSection}>
            <h2 className={s.title}>Выйти из аккаунта</h2>
            <span className={s.subtitle}>Вы завершите текущую сессию и вернетесь на экран входа</span>
          </div>

          <div className={s.actions}>
            <Button
              variant="outline"
              color="green"
              width="auto"
              textSize="b2"
              height="66px"
              className={s.cancelBtn}
              onClick={onClose}
            >
              Отмена
            </Button>
            <Button color="green" width="auto" textSize="b2" height="66px" className={s.logoutBtn} onClick={logout}>
              Выйти
            </Button>
          </div>
        </div>
      </div>
      <Overlay onClose={onClose} isOpen={open} />
    </>,
    document.body,
  );
};
